import { consentLedgerApi } from "@/lib/api";

/**
 * CONSENT-LEDGER-1 — the real consent transport, over the app's own HTTP client.
 *
 * It implements the two methods `consentLedger.js` injects (see its contract at the top of that
 * file): `postEvent(payload)` and `getStatus(deviceId)`. The payload is already exactly what
 * `recordConsentEvent` builds, so no call site changes.
 *
 * WHY THE AXIOS CLIENT AND NOT `fetch`, which is where the handback spec's reference
 * implementation would have failed in production:
 *
 * This app authenticates with a BEARER TOKEN read from localStorage and attached by the axios
 * request interceptor (lib/apiClient.js:35-42). `fetch` with `credentials: "include"` sends
 * COOKIES — it does not send that header. So a raw-fetch transport would have reached
 * `/api/consents/events` with no Authorization at all, the route depends on `get_current_user`,
 * and every post would have come back 401. Under the strict `recorded === true` rule that lands in
 * this same commit, a 401 means NOT RECORDED, which means EVERY CAMERA REFUSED in production — the
 * precise failure the one-PR rule exists to prevent, arriving through a different door.
 *
 * The only raw-fetch call in this codebase (reflectiveApi.respondStream) exists because axios
 * cannot stream, and it has to re-attach the Bearer token and the preview header by hand "to mirror
 * the axios interceptor". There is no reason to take on that duplication here, and two reasons not
 * to: a master admin previewing a teacher needs the X-Cognivio-Preview-User header the interceptor
 * adds, and the 401 handling in that interceptor is what keeps a stale token from looking like a
 * consent failure.
 *
 * ON FAILURE IT REJECTS, which the spec allows explicitly ("REJECT or return a falsy-ok shape").
 * axios rejects on any non-2xx, and both call sites in `consentLedger.js` already catch: a failed
 * post resolves to `false` (fail closed, the camera stays shut and she is told), and a failed status
 * resolves to "ask her again". Every uncertain answer still lands on the safe side.
 */
export function createHttpConsentTransport({ client = consentLedgerApi } = {}) {
  return {
    /**
     * @returns the parsed body: { ok, recorded, event_id, ts, version }. `recorded` is the only
     * field that may open a camera, and the server sets it true only after the insert returned.
     */
    async postEvent(payload) {
      const res = await client.postEvent(payload);
      return res?.data ?? {};
    },

    /** @returns { camera_microphone: { state, version, scope, expires_at }, … } */
    async getStatus(deviceId) {
      const res = await client.status(deviceId);
      return res?.data ?? {};
    },
  };
}

export default createHttpConsentTransport;
